import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import type { BoxProps, DeviceType } from '@talos/kyoko';
import {
  Box,
  Divider,
  DropdownMenu,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Text,
  Tooltip,
  Tour,
  notEmpty,
  useDisclosure,
  useMixpanel,
} from '@talos/kyoko';
import type { CSSProperties } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RouteMatch } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { toNavGroupName } from '../../hooks/useSpacesNavItems';
import { useAppLayoutConfig } from '../../providers/AppLayoutConfig/AppLayoutConfigContext';
import { useSpacesConfig } from '../../providers/AppLayoutConfig/SpaceConfigContext';
import { useAppStateDispatch, useAppStateSelector, useAppStateStore } from '../../providers/AppStateProvider';
import { groupAcceptsSpaceDrops, type NavigationItem } from '../../tokens/navigationPaths';
import { ShowcaseName } from '../../tokens/showcase';
import { TourName } from '../../tokens/tours';
import type { LayoutSpec } from '../../types/LayoutConfig';
import { getPinSection } from '../../utils/getPinSection';
import { addNewLayoutToSection, selectCurrentLayout } from '../AppLayout/AppLayoutSlice';
import type { LayoutSpecResponse } from '../AppLayout/LayoutSpecs/LayoutSpecSlice';
import { ShowcaseTrigger } from '../Showcase';
import { getInitialAddSpaceGroup } from './getInitialAddSpaceGroup';
import { useSectionSpaceMetadata } from './hooks/useSectionSpaceMetadata';
import {
  applyReorder,
  emptyGroupDropId,
  findGroupKeyForId,
  getGroupLabelForSave,
  getInitialOrderByGroup,
  getNavigationGroupKey,
  parseEmptyGroupDropId,
} from './navigationSidebarReorder';
import { parseSection } from './parseSection';
import { PinMenu } from './PinMenu';
import { SidebarSubMenuNavItem } from './SidebarSubMenuNavItem';
import { getLayoutSpecForNavItem } from './spaceLayoutLookup';
import { ManageSpaceButtons } from './Spaces/ManageSpaceButtons';
import { ManageSpaceModal } from './Spaces/ManageSpaceModal/ManageSpaceModal';
import { ManageTemplateModal } from './Spaces/ManageTemplateModal/ManageTemplateModal';
import { getLocationAfterSpaceGroupChange, getSpaceBasePath } from './spacesNavPath';
import {
  SidebarSubMenu,
  SidebarSubMenuEmptyGroupDrop,
  SidebarSubMenuGroupHeader,
  SidebarSubMenuHeader,
  SidebarSubMenuItems,
  SidebarSubMenuNavItemRowStatic,
  SidebarSubMenuTitle,
  SidebarSubMenuWrapper,
} from './styles';

const PIN_MENU_TARGET_STYLE: CSSProperties = {
  marginRight: '-8px',
  marginLeft: '-8px',
  zIndex: 0,
};

const PIN_TARGET_STYLE: CSSProperties = {
  zIndex: 1,
};

const PIN_MENU_DROPDOWN_PROPS = {
  'data-testid': 'pin-menu',
  'data-sidebar-portal-interaction': true,
} as BoxProps & { 'data-testid': string };

function EmptyGroupDropZone({ groupKey }: { groupKey: string }) {
  const { setNodeRef, isOver } = useDroppable({
    id: emptyGroupDropId(groupKey),
  });
  return (
    <SidebarSubMenuEmptyGroupDrop
      ref={setNodeRef}
      data-testid="sidebar-submenu-empty-group-drop"
      $isOver={isOver}
      aria-hidden
    />
  );
}

export interface NavigationSidebarSubMenuProps {
  /** Mount submenu content (equivalent to `showSubMenu || isPinned` on shell). */
  visible: boolean;
  /** Passed to `SidebarSubMenuWrapper` `expanded` (e.g. `hasSubNavItems && isPinned`). */
  wrapperExpanded: boolean;
  /** Sidebar hover expanded — for tour delay / pin row tour visibility. */
  isExpanded: boolean;
  /** Whether the active top-level route has child nav items. */
  hasSubNavItems: boolean;
  /**
   * Route matches from the parent — do not call `useNavigationItems` here: it runs `useSpacesNavItems`
   * (which updates app config from layout defaults) and must run only from the shell that mounts once.
   */
  topLevelMatch: RouteMatch<string, NavigationItem> | undefined;
  groupMatch: RouteMatch<string, NavigationItem> | undefined;
  onSpaceModalOpenChange?: (isOpen: boolean) => void;
}

export const NavigationSidebarSubMenu = ({
  visible,
  wrapperExpanded,
  isExpanded,
  hasSubNavItems,
  topLevelMatch,
  groupMatch,
  onSpaceModalOpenChange,
}: NavigationSidebarSubMenuProps) => {
  const { pinNavigationSidebar, setPinNavigationSidebar } = useDisplaySettings();
  const location = useLocation();
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  const layoutSection = parseSection(topLevelMatch?.route?.path);
  const pinSection = getPinSection(location.pathname);

  const [isRearranging, setIsRearranging] = useState(false);
  const [pendingOrderByGroup, setPendingOrderByGroup] = useState<Map<string, string[]>>(() => new Map());
  const [pendingGroups, setPendingGroups] = useState<Map<string, string | undefined>>(new Map());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const pendingOrderByGroupRef = useRef(pendingOrderByGroup);
  const pendingGroupsRef = useRef(pendingGroups);

  useEffect(() => {
    pendingOrderByGroupRef.current = pendingOrderByGroup;
  }, [pendingOrderByGroup]);

  useEffect(() => {
    pendingGroupsRef.current = pendingGroups;
  }, [pendingGroups]);

  const [editSpaceItem, setEditSpaceItem] = useState<NavigationItem | null>(null);
  const [manageTemplateItemId, setManageTemplateItemId] = useState<string | undefined>(undefined);
  const [manageTemplateConfig, setManageTemplateConfig] = useState<LayoutSpecResponse | undefined>(undefined);

  const addSpaceDisclosure = useDisclosure();
  const editSpaceDisclosure = useDisclosure();
  const manageTemplateDisclosure = useDisclosure({
    onClose() {
      setManageTemplateConfig(undefined);
    },
  });
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const previousLayoutSectionRef = useRef(layoutSection);

  // If browser back/forward changes sections while a modal is open, clear modal state to avoid locking the nav.
  useEffect(() => {
    if (previousLayoutSectionRef.current === layoutSection) {
      return;
    }

    previousLayoutSectionRef.current = layoutSection;
    addSpaceDisclosure.close();
    editSpaceDisclosure.close();
    manageTemplateDisclosure.close();
    setEditSpaceItem(null);
    setManageTemplateItemId(undefined);
    setManageTemplateConfig(undefined);
    setRemoveDialogOpen(false);
    setIsRearranging(false);
    setActiveDragId(null);
  }, [addSpaceDisclosure, editSpaceDisclosure, layoutSection, manageTemplateDisclosure]);

  const anySpaceModalOpen =
    addSpaceDisclosure.isOpen ||
    editSpaceDisclosure.isOpen ||
    manageTemplateDisclosure.isOpen ||
    removeDialogOpen ||
    isRearranging;
  useEffect(() => {
    onSpaceModalOpenChange?.(anySpaceModalOpen);
  }, [anySpaceModalOpen, onSpaceModalOpenChange]);

  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const navigate = useNavigate();
  const store = useAppStateStore();

  const { layoutConfigs, updateLayoutConfig, updateSelectedLayoutId, updateSpacesConfigs } = useAppLayoutConfig(
    layoutSection ?? 'trading'
  );
  const { updateSpace } = useSpacesConfig();
  const currentLayout = useAppStateSelector(selectCurrentLayout);
  const { existingGroups, sectionLayoutIdsAndLabels } = useSectionSpaceMetadata(layoutSection ?? 'trading');
  const initialAddSpaceGroup = useMemo(
    () => getInitialAddSpaceGroup(topLevelMatch?.route?.children, location.pathname, currentLayout?.group),
    [topLevelMatch?.route?.children, location.pathname, currentLayout?.group]
  );
  const availableGroups = useMemo(
    () => [
      ...new Set([
        ...existingGroups,
        ...((topLevelMatch?.route?.children ?? []) as NavigationItem[]).map(group => group.label).filter(notEmpty),
      ]),
    ],
    [existingGroups, topLevelMatch?.route?.children]
  );

  const isPinned = pinNavigationSidebar;

  const handlePinChange = useCallback(() => {
    mixpanel.track(MixpanelEvent.ChangeSidebarPin, { pinned: !pinNavigationSidebar, section: pinSection });
    if (pinSection !== '') {
      setPinNavigationSidebar(!isPinned);
    }
  }, [pinNavigationSidebar, mixpanel, pinSection, setPinNavigationSidebar, isPinned]);

  const handleAddNewSpace = useCallback(
    (newSpaceName: string, selectedTemplate: LayoutSpec, deviceType: DeviceType, group?: string) => {
      if (newSpaceName === '') {
        return;
      }
      dispatch(addNewLayoutToSection({ label: newSpaceName, template: selectedTemplate, deviceType, group }));
      const newLayout = selectCurrentLayout(store.getState());
      if (newLayout && layoutSection) {
        updateLayoutConfig(newLayout);
        updateSelectedLayoutId(newLayout.id);
        const navGroupSlug = group ? toNavGroupName(group) : undefined;
        navigate(getSpaceBasePath(layoutSection, navGroupSlug, newLayout.id));
      }
      mixpanel.track(MixpanelEvent.AddNewSpace, {
        [MixpanelEventProperty.Group]: group,
        [MixpanelEventProperty.LayoutSection]: layoutSection,
        [MixpanelEventProperty.DeviceType]: deviceType,
      });
    },
    [mixpanel, navigate, dispatch, store, layoutSection, updateLayoutConfig, updateSelectedLayoutId]
  );

  const handleUpdateSpace = useCallback(
    (id: string, config: Partial<LayoutSpec>) => {
      if (!layoutSection) {
        return;
      }
      updateSpace(layoutSection, id, config);
      mixpanel.track(MixpanelEvent.UpdateSpace, {
        [MixpanelEventProperty.LayoutSection]: layoutSection,
        [MixpanelEventProperty.Group]: config.group,
        [MixpanelEventProperty.DeviceType]: config.deviceType,
      });
      if (editSpaceItem?.id && 'group' in config) {
        const itemPath = editSpaceItem.path;
        const currentLocation = locationRef.current;
        const currentPathname = currentLocation.pathname;
        const isOnThisSpace =
          itemPath != null && (currentPathname === itemPath || currentPathname.startsWith(itemPath + '/'));
        if (isOnThisSpace && itemPath != null) {
          const newGroup = config.group;
          const newSpacePath = newGroup
            ? `/${layoutSection}/${toNavGroupName(newGroup)}/${id}`
            : `/${layoutSection}/${id}`;
          const nextPathname = `${newSpacePath}${currentPathname.slice(itemPath.length)}`;
          if (nextPathname !== currentPathname) {
            navigate({ pathname: nextPathname, search: currentLocation.search, hash: currentLocation.hash });
          }
        }
      }
    },
    [updateSpace, mixpanel, layoutSection, editSpaceItem, navigate]
  );

  const handleEditTemplate = useCallback(
    (config: LayoutSpecResponse) => {
      const isEditingSpace = editSpaceDisclosure.isOpen;
      const layoutId = isEditingSpace ? editSpaceItem?.id : currentLayout?.id;
      editSpaceDisclosure.close();
      addSpaceDisclosure.close();
      setManageTemplateItemId(layoutId);
      setManageTemplateConfig(config);
      mixpanel.track(MixpanelEvent.OpenEditTemplateModal, {
        [MixpanelEventProperty.LayoutSection]: layoutSection,
        [MixpanelEventProperty.Source]: isEditingSpace
          ? MixpanelEventSource.NavigationSidebarSubNavItem
          : MixpanelEventSource.NavigationSidebar,
      });
      manageTemplateDisclosure.open();
    },
    [
      editSpaceDisclosure,
      addSpaceDisclosure,
      manageTemplateDisclosure,
      editSpaceItem,
      currentLayout,
      mixpanel,
      layoutSection,
    ]
  );

  const handleOpenEditSpace = useCallback(
    (item: NavigationItem) => {
      if (!layoutSection) {
        return;
      }
      setEditSpaceItem(item);
      editSpaceDisclosure.open();
    },
    [layoutSection, editSpaceDisclosure]
  );

  const handleOpenManageTemplate = useCallback(
    (item: NavigationItem, selectedTemplateConfig?: LayoutSpecResponse) => {
      if (!layoutSection) {
        return;
      }
      setManageTemplateItemId(item.id);
      setManageTemplateConfig(selectedTemplateConfig);
      manageTemplateDisclosure.open();
    },
    [layoutSection, manageTemplateDisclosure]
  );

  const handleOpenAddSpace = useCallback(() => {
    addSpaceDisclosure.open();
  }, [addSpaceDisclosure]);

  const handleOpenCreateTemplate = useCallback(() => {
    const currentLayoutId = currentLayout?.id;
    if (!layoutSection || currentLayoutId == null) {
      return;
    }
    setManageTemplateItemId(currentLayoutId);
    setManageTemplateConfig(undefined);
    manageTemplateDisclosure.open();
  }, [layoutSection, currentLayout?.id, manageTemplateDisclosure]);

  const editInitialLayoutSpec = getLayoutSpecForNavItem({
    item: editSpaceItem,
    sectionLayoutConfigs: layoutConfigs,
  });

  const editableItems = useMemo(() => {
    const items: NavigationItem[] = [];
    for (const group of topLevelMatch?.route?.children ?? []) {
      for (const item of group.children ?? []) {
        if (item.isEditable) {
          items.push(item);
        }
      }
    }
    return items;
  }, [topLevelMatch?.route?.children]);

  const dragOverlayItem = useMemo(
    () => (activeDragId != null ? editableItems.find(i => (i.id ?? i.path) === activeDragId) : undefined),
    [activeDragId, editableItems]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const handleStartRearranging = useCallback(() => {
    const layoutById = Object.fromEntries((layoutConfigs ?? []).map(l => [l.id, l])) as Record<string, LayoutSpec>;
    const pendingGroupsMap = new Map(
      editableItems.map(item => {
        const id = item.id ?? '';
        return [item.id ?? item.path ?? '', id ? layoutById[id]?.group : undefined];
      })
    );
    const orderByGroup = getInitialOrderByGroup(topLevelMatch?.route?.children);
    pendingOrderByGroupRef.current = orderByGroup;
    pendingGroupsRef.current = pendingGroupsMap;
    setPendingOrderByGroup(orderByGroup);
    setPendingGroups(pendingGroupsMap);
    setIsRearranging(true);
    mixpanel.track(MixpanelEvent.StartSpacesNavRearrange, {
      [MixpanelEventProperty.LayoutSection]: layoutSection,
    });
  }, [editableItems, layoutConfigs, layoutSection, mixpanel, topLevelMatch?.route?.children]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const groupKeyAcceptsSpaceDrops = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const g of topLevelMatch?.route?.children ?? []) {
      m.set(getNavigationGroupKey(g), groupAcceptsSpaceDrops(g));
    }
    return m;
  }, [topLevelMatch?.route?.children]);

  const groupLabelsByKey = useMemo(() => {
    const m = new Map<string, string | undefined>();
    for (const g of topLevelMatch?.route?.children ?? []) {
      m.set(getNavigationGroupKey(g), g.label);
    }
    return m;
  }, [topLevelMatch?.route?.children]);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) {
        return;
      }
      const overIdStr = String(over.id);
      const emptyDropKey = parseEmptyGroupDropId(overIdStr);
      if (emptyDropKey != null) {
        if (!groupKeyAcceptsSpaceDrops.get(emptyDropKey)) {
          return;
        }
      } else {
        const overKey = findGroupKeyForId(pendingOrderByGroupRef.current, overIdStr);
        if (overKey != null && !groupKeyAcceptsSpaceDrops.get(overKey)) {
          return;
        }
      }
      const result = applyReorder(
        pendingOrderByGroupRef.current,
        pendingGroupsRef.current,
        String(active.id),
        overIdStr,
        groupLabelsByKey
      );
      if (!result) {
        return;
      }
      pendingOrderByGroupRef.current = result.orderByGroup;
      pendingGroupsRef.current = result.groups;
      setPendingOrderByGroup(result.orderByGroup);
      setPendingGroups(result.groups);
    },
    [groupKeyAcceptsSpaceDrops, groupLabelsByKey]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      handleDragOver(event);
      mixpanel.track(MixpanelEvent.MovedSpacesNavItem, {
        [MixpanelEventProperty.LayoutSection]: layoutSection,
      });
      setActiveDragId(null);
    },
    [handleDragOver, layoutSection, mixpanel]
  );

  const handleSaveRearrange = useCallback(() => {
    if (!layoutConfigs || !layoutSection) {
      setIsRearranging(false);
      return;
    }
    const orderedIds: string[] = [];
    const orderedIdSet = new Set<string>();
    for (const g of topLevelMatch?.route?.children ?? []) {
      for (const id of pendingOrderByGroupRef.current.get(getNavigationGroupKey(g)) ?? []) {
        if (!orderedIdSet.has(id)) {
          orderedIds.push(id);
          orderedIdSet.add(id);
        }
      }
    }
    for (const l of layoutConfigs) {
      if (!orderedIdSet.has(l.id)) {
        orderedIds.push(l.id);
        orderedIdSet.add(l.id);
      }
    }
    const layoutByIdForSave = Object.fromEntries(layoutConfigs.map(l => [l.id, l])) as Record<string, LayoutSpec>;
    const pendingGroups = pendingGroupsRef.current;

    const nextLayouts = orderedIds
      .map(id => {
        const l = layoutByIdForSave[id];
        if (l == null) {
          return null;
        }
        const g = getGroupLabelForSave(id, pendingOrderByGroupRef.current, pendingGroups, groupLabelsByKey, l.group);
        const { group: _prev, ...rest } = l;
        return g != null && g !== '' ? ({ ...rest, group: g } as LayoutSpec) : (rest as LayoutSpec);
      })
      .filter(notEmpty);

    updateSpacesConfigs(nextLayouts);
    mixpanel.track(MixpanelEvent.SaveSpacesNavRearrange, {
      [MixpanelEventProperty.LayoutSection]: layoutSection,
    });

    if (topLevelMatch?.route?.enableSpaces && layoutSection && layoutConfigs?.length) {
      const nextLocation = getLocationAfterSpaceGroupChange(
        location.pathname,
        location.search,
        location.hash,
        layoutSection,
        nextLayouts
      );
      if (nextLocation) {
        navigate(nextLocation, { replace: true });
      }
    }

    setIsRearranging(false);
  }, [
    layoutConfigs,
    layoutSection,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    groupLabelsByKey,
    topLevelMatch?.route?.children,
    topLevelMatch?.route?.enableSpaces,
    updateSpacesConfigs,
    mixpanel,
  ]);

  const handleCancelRearrange = useCallback(() => {
    mixpanel.track(MixpanelEvent.CancelSpacesNavRearrange, {
      [MixpanelEventProperty.LayoutSection]: layoutSection,
    });
    setIsRearranging(false);
    setPendingOrderByGroup(new Map());
    setPendingGroups(new Map());
    setActiveDragId(null);
  }, [layoutSection, mixpanel]);

  useEffect(() => {
    setIsRearranging(false);
    setPendingOrderByGroup(new Map());
    setPendingGroups(new Map());
    setActiveDragId(null);
  }, [layoutSection]);

  const getGroupItems = useCallback(
    (group: NavigationItem): NavigationItem[] => {
      const originalItems = group.children ?? [];
      if (!isRearranging) {
        return originalItems;
      }

      const groupKey = getNavigationGroupKey(group);
      const idOrder = pendingOrderByGroup.get(groupKey) ?? [];
      if (idOrder.length === 0 && originalItems.every((i: NavigationItem) => !i.isEditable)) {
        return originalItems;
      }

      const nonEditable = originalItems.filter((i: NavigationItem) => !i.isEditable);

      const byId = new Map(editableItems.map(item => [item.id ?? item.path ?? '', item] as const));
      const editable = idOrder.map(id => byId.get(id)).filter(notEmpty);

      return [...nonEditable, ...editable];
    },
    [isRearranging, editableItems, pendingOrderByGroup]
  );

  const renderSubMenuItems = () => {
    const groups = topLevelMatch?.route?.children ?? [];
    const content = groups
      .map((group: NavigationItem) => {
        const groupItems = getGroupItems(group);

        const navItem = (item: NavigationItem) => (
          <SidebarSubMenuNavItem
            key={item.path}
            item={item}
            layoutSection={layoutSection}
            rearrangeModeActive={isRearranging}
            onStartRearranging={handleStartRearranging}
            onOpenEditSpace={handleOpenEditSpace}
            onOpenManageTemplate={handleOpenManageTemplate}
            onRemoveDialogOpenChange={setRemoveDialogOpen}
          />
        );

        const groupKey = getNavigationGroupKey(group);
        const acceptsDrops = groupKeyAcceptsSpaceDrops.get(groupKey) ?? false;
        const nonEditable = groupItems.filter((i: NavigationItem) => !i.isEditable);
        const editable = groupItems.filter((i: NavigationItem) => i.isEditable);
        const sortableIdsForGroup = pendingOrderByGroup.get(groupKey) ?? [];

        const nonEditableItems = nonEditable.map(navItem);
        const needsEmptyGroupDropZone = sortableIdsForGroup.length === 0 && acceptsDrops;

        return (
          <Fragment key={group.path ?? group.label ?? 'undefined'}>
            {group.label && (
              <SidebarSubMenuGroupHeader
                data-testid="sidebar-submenu-group-header"
                data-active={groupMatch?.route?.path === group.path}
                active={groupMatch?.route?.path === group.path}
                onClick={() => {
                  mixpanel.track(MixpanelEvent.NavigateTo, {
                    path: group.path,
                    source: MixpanelEventSource.NavigationSidebar,
                  });
                  if (group.mixpanelEvent) {
                    mixpanel.track(group.mixpanelEvent);
                  }
                }}
              >
                {group.label}
              </SidebarSubMenuGroupHeader>
            )}

            {isRearranging ? (
              <SortableContext items={sortableIdsForGroup} strategy={verticalListSortingStrategy}>
                {nonEditableItems}
                {editable.map(navItem)}
                {needsEmptyGroupDropZone && <EmptyGroupDropZone groupKey={groupKey} />}
              </SortableContext>
            ) : (
              <>
                {nonEditableItems}
                {editable.map(navItem)}
              </>
            )}
          </Fragment>
        );
      })
      .filter(notEmpty);

    if (isRearranging) {
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {content}
          <DragOverlay dropAnimation={null}>
            {dragOverlayItem ? (
              <SidebarSubMenuNavItemRowStatic
                style={{
                  cursor: 'grabbing',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
                  opacity: 0.96,
                }}
              >
                <Box flex="1 1 0" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  <Text>{dragOverlayItem.label}</Text>
                </Box>
              </SidebarSubMenuNavItemRowStatic>
            ) : null}
          </DragOverlay>
        </DndContext>
      );
    }

    return content;
  };

  return (
    <>
      <SidebarSubMenuWrapper data-testid="sidebar-submenu-wrapper" expanded={wrapperExpanded}>
        {visible && (
          <SidebarSubMenu data-testid="sidebar-submenu">
            <SidebarSubMenuHeader>
              <SidebarSubMenuTitle data-testid="sidebar-submenu-title">
                {topLevelMatch?.route?.label}
              </SidebarSubMenuTitle>
              {hasSubNavItems && (isExpanded || isPinned) && (
                <Tour tour={TourName.v255UpdatedNavigationSidebarPin} delay={5000} />
              )}
              <HStack>
                <Tooltip
                  tooltip={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
                  placement="left"
                  targetStyle={PIN_TARGET_STYLE}
                >
                  <IconButton
                    data-testid="sidebar-pin-button"
                    data-pin-state={isPinned ? 'pinned' : 'unpinned'}
                    size={FormControlSizes.Small}
                    ghost
                    dim
                    icon={isPinned ? IconName.Pin : IconName.PinOff}
                    onClick={handlePinChange}
                  />
                </Tooltip>
                <DropdownMenu
                  triggerElement={
                    <IconButton
                      data-testid="sidebar-pin-menu-trigger"
                      ghost
                      size={FormControlSizes.Small}
                      dim
                      icon={IconName.TriangleDown}
                    />
                  }
                  trigger="click"
                  placement="bottom-end"
                  usePortal
                  targetStyle={PIN_MENU_TARGET_STYLE}
                  dropdownProps={PIN_MENU_DROPDOWN_PROPS}
                >
                  <PinMenu />
                </DropdownMenu>
              </HStack>
            </SidebarSubMenuHeader>
            <SidebarSubMenuItems
              data-testid="sidebar-submenu-items"
              {...(layoutSection === 'intel' ? { id: 'intel-sidebar-submenu-items' } : {})}
            >
              {renderSubMenuItems()}
              {topLevelMatch?.route?.enableSpaces && (
                <>
                  <ShowcaseTrigger showcase={ShowcaseName.v256TradingSpaces} />
                  <Divider my="spacing8" color="backgroundDivider" />
                  {layoutSection && (
                    <ManageSpaceButtons
                      layoutSection={layoutSection}
                      isRearranging={isRearranging}
                      onSaveRearrange={handleSaveRearrange}
                      onCancelRearrange={handleCancelRearrange}
                      onOpenAddSpace={handleOpenAddSpace}
                      onOpenCreateTemplate={handleOpenCreateTemplate}
                    />
                  )}
                </>
              )}
            </SidebarSubMenuItems>
          </SidebarSubMenu>
        )}
      </SidebarSubMenuWrapper>
      {addSpaceDisclosure.isOpen && layoutSection && (
        <ManageSpaceModal
          onAddNewSpace={handleAddNewSpace}
          layoutSection={layoutSection}
          onEditTemplate={handleEditTemplate}
          initialGroup={initialAddSpaceGroup}
          existingGroups={availableGroups}
          sectionLayoutIdsAndLabels={sectionLayoutIdsAndLabels}
          {...addSpaceDisclosure}
        />
      )}
      {editSpaceDisclosure.isOpen && editInitialLayoutSpec && layoutSection && (
        <ManageSpaceModal
          layoutSection={layoutSection}
          onEditTemplate={handleEditTemplate}
          onUpdateSpace={handleUpdateSpace}
          initialLayoutSpec={editInitialLayoutSpec}
          existingGroups={availableGroups}
          sectionLayoutIdsAndLabels={sectionLayoutIdsAndLabels}
          {...editSpaceDisclosure}
        />
      )}
      {manageTemplateDisclosure.isOpen && layoutSection && (
        <ManageTemplateModal
          layoutSection={layoutSection}
          initialLayoutId={manageTemplateItemId}
          selectedTemplateConfig={manageTemplateConfig}
          {...manageTemplateDisclosure}
        />
      )}
    </>
  );
};
